import React, { useState } from 'react';
import axios from 'axios';
import Modal from './Modal';
import { downloadFile } from '../lib/assets';
import './Assets.css';

/**
 * Bring a spreadsheet of devices in: template, file, preview, confirm.
 *
 * The preview is the whole point. The server reads the file and says, row by
 * row, what it would do - add, update by serial number, leave alone, or
 * refuse with a reason - and writes nothing. Only "Import" writes, and it
 * sends the same file again so the server checks it once more against the
 * register as it is at that moment.
 *
 * A file with problems is refused as a whole unless IT ticks "leave those
 * rows out", which only appears once the problems have been shown.
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ACTION = {
  create: ['Add', 'success'], update: ['Update', 'info'],
  unchanged: ['No change', 'idle'], error: ['Problem', 'critical']
};

export default function AssetImport({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [skip, setSkip] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  const send = (path) => {
    const body = new FormData();
    body.append('file', file);
    if (skip) body.append('skip_errors', 'true');
    return axios.post(`${API_BASE}${path}`, body, { headers: { 'Content-Type': 'multipart/form-data' } });
  };

  const read = async () => {
    setBusy(true); setError(''); setPreview(null); setSkip(false);
    try { setPreview((await send('/assets/import/preview')).data); } catch (e) {
      setError(e.response?.data?.error || 'Could not read that file.');
    } finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true); setError('');
    try { setDone((await send('/assets/import/commit')).data); } catch (e) {
      if (e.response?.status === 409 && e.response?.data?.rows) setPreview(e.response.data);
      setError(e.response?.data?.error || 'Could not import that file.');
    } finally { setBusy(false); }
  };

  const s = preview?.summary;
  const canCommit = s && (s.create + s.update > 0) && (s.error === 0 || skip);

  if (done) {
    return (
      <Modal title="Imported" onClose={() => onDone(done)}
        footer={<button type="button" className="btn btn-primary" onClick={() => onDone(done)}>Done</button>}>
        <p>
          {`${done.created} device${done.created === 1 ? '' : 's'} added and ${done.updated} updated from ${done.file_name}.`}
          {done.unchanged ? ` ${done.unchanged} already matched the register.` : ''}
          {done.skipped ? ` ${done.skipped} row${done.skipped === 1 ? ' was' : 's were'} left out.` : ''}
        </p>
        <p className="as-quiet">Every device’s history says it came from this file.</p>
      </Modal>
    );
  }

  return (
    <Modal
      wide
      title="Import devices"
      subtitle="Nothing is written until you have seen what the file would do."
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          {preview
            ? <button type="button" className="btn btn-primary" disabled={!canCommit || busy} onClick={commit}>
                {busy ? 'Importing…' : `Import ${s.create + s.update} row${s.create + s.update === 1 ? '' : 's'}`}
              </button>
            : <button type="button" className="btn btn-primary" disabled={!file || busy} onClick={read}>
                {busy ? 'Reading…' : 'Check the file'}
              </button>}
        </>
      )}
    >
      <ol className="as-steps">
        <li>
          Download the template:{' '}
          <button type="button" className="btn btn-plain btn-small"
            onClick={() => downloadFile('/assets/template', 'jarvis-devices-template.xlsx')}>Excel</button>
          <button type="button" className="btn btn-plain btn-small"
            onClick={() => downloadFile('/assets/template?format=csv', 'jarvis-devices-template.csv')}>CSV</button>
          <span className="as-quiet"> — the second sheet lists the categories and places it accepts.</span>
        </li>
        <li>Fill in one row per device. Make, model and serial number are enough; the serial is how a corrected file finds the device again.</li>
        <li>
          <label htmlFor="as-import-file">Choose the file </label>
          <input id="as-import-file" type="file" accept=".xlsx,.csv,text/csv"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setError(''); }} />
        </li>
      </ol>

      {error && <p className="as-error" role="alert">{error}</p>}

      {preview && (
        <>
          <p className="as-import-summary">
            {[['create', 'to add'], ['update', 'to update'], ['unchanged', 'already right'], ['error', 'with problems']]
              .filter(([k]) => s[k]).map(([k, w]) => (
                <span key={k} className="as-chip" data-tone={ACTION[k][1]}>{`${s[k]} ${w}`}</span>
              ))}
          </p>
          {preview.unknown_headers?.length > 0 && (
            <p className="as-note" data-tone="warning">
              {`These columns are not in the template and were ignored: ${preview.unknown_headers.join(', ')}.`}
            </p>
          )}
          {s.error > 0 && (
            <label className="as-check">
              <input type="checkbox" checked={skip} onChange={(e) => setSkip(e.target.checked)} />
              Leave the rows with problems out and import the rest
            </label>
          )}
          <div className="as-table-wrap" style={{ maxHeight: '22rem', overflowY: 'auto', marginTop: '0.75rem' }}>
            <table className="as-table">
              <thead><tr><th>Row</th><th>What happens</th><th>Device</th><th>Details</th></tr></thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr key={r.line} style={{ cursor: 'default' }}>
                    <td data-label="Row">{r.line}</td>
                    <td data-label="What happens">
                      <span className="as-chip" data-tone={ACTION[r.action][1]}>{ACTION[r.action][0]}</span>
                    </td>
                    <td data-label="Device">{r.asset_tag ? <span className="as-tag">{r.asset_tag}</span> : null} {r.label}</td>
                    <td data-label="Details">
                      {r.action === 'error' && r.errors.join('; ')}
                      {r.action === 'update' && `Changes: ${Object.keys(r.values).map((k) => k.replace(/_id$/, '').replace(/_/g, ' ')).join(', ')}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  );
}
